import { useContext, useMemo } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Container from 'components/Container';
import { SEO } from 'components/SEO';
import { Contact } from 'pages/Main/components';
import {
  Hero,
  MainSection,
  SubSectionContainer,
  SubSection,
  TeamContainer,
  TeamMember,
} from './components';
import { SchoolContext, SchoolLocation } from 'context/SchoolContext';
import Stack from '@mui/material/Stack';


function About(): JSX.Element {
  const schoolContext = useContext(SchoolContext);

  // Generate keywords for About page
  const aboutKeywords = useMemo(() => {
    const baseKeywords = [
      'about us',
      'driving school about',
      'driver education team',
      'driving instructors',
      'driving school history',
      'certified driving instructors',
      'professional driver training',
    ];

    if (schoolContext?.name) {
      baseKeywords.push(
        `about ${schoolContext.name}`,
        `${schoolContext.name} driving school`,
        `${schoolContext.name} instructors`
      );
    }

    if (schoolContext?.locations) {
      schoolContext.locations.forEach(location => {
        if (location.city) {
          baseKeywords.push(`driving school ${location.city}`);
        }
      });
    }

    return baseKeywords;
  }, [schoolContext]);

  return (
    <>
      <SEO
        title="About Us"
        description={
          schoolContext?.about.subtitle ||
          schoolContext?.about.mainSectionContent ||
          `Learn about our professional driving school, certified instructors, and commitment to safe driver education. ${schoolContext?.name || 'Our driving school'} provides quality driver training with experienced instructors.`
        }
        keywords={aboutKeywords}
        ogType="website"
        ogImage={schoolContext?.about.bannerImage || schoolContext?.logoImage}
        ogImageAlt={`About ${schoolContext?.name || 'Our Driving School'}`}
      />
      <Hero
        title={schoolContext?.about.title}
        subtitle={schoolContext?.about.subtitle}
        image={schoolContext?.about.bannerImage}
      />
      <Container>
        <MainSection
          title={schoolContext?.about.mainSectionTitle ?? ''}
          content={schoolContext?.about.mainSectionContent ?? ''}
          // image={schoolContext?.about.mainSectionImage}
          image={schoolContext?.logoImage}
        />
      </Container>
      {schoolContext?.about.subsections && (
        <Container paddingTop={'0 !important'}>
          <SubSectionContainer>
            {schoolContext?.about.subsections?.map((subsection, index) => (
              <SubSection title={subsection.title} content={subsection.content} key={index} />
            ))}
          </SubSectionContainer>
        </Container>
      )}
      <Container maxWidth={800} paddingY={'0 !important'}>
        <Divider />
      </Container>
      {schoolContext?.about.teamMembers && schoolContext?.about.teamMembers.length > 0 && (
        <Box bgcolor={'alternate.main'}>
          <Container>
            <TeamContainer>
              {schoolContext?.about.teamMembers?.map((member) => (
                <TeamMember
                  key={member.name}
                  name={member.name}
                  title={member.title}
                  avatar={member.avatar}
                  about={member.about}
                />
              ))}
            </TeamContainer>
          </Container>
        </Box>
      )}
      <Container>
        <Stack gap={2} divider={<Divider flexItem />}>
          {schoolContext?.locations?.map((location: SchoolLocation, index: number) => (<Contact {...location} key={index} />))}
        </Stack>
      </Container>
      {/* <Container>
        <Gallery />
      </Container>
      <Container maxWidth={800} paddingTop={'0 !important'}>
        <Application />
      </Container> */}
    </>
  );
}

export default About;
